import type { JSX, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '../lib/cn';

export interface EmptyStateProps {
  /** Decorative: aria-hidden. Sits inside a soft circular badge. */
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode;
  headingLevel?: 'h1' | 'h2';
  className?: string;
}

/**
 * A placeholder for empty listings and "coming soon" screens: icon badge,
 * title, description, optional action \u2014 all composable, nothing forced.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  headingLevel = 'h2',
  className,
}: EmptyStateProps): JSX.Element {
  const Heading = headingLevel;

  return (
    <div className={cn('flex flex-col items-center gap-3 px-6 py-12 text-center', className)}>
      {Icon === undefined ? null : (
        <span className="flex size-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
          <Icon aria-hidden="true" className="size-6" />
        </span>
      )}

      <div className="flex flex-col gap-1">
        <Heading className="text-base font-semibold text-slate-900">{title}</Heading>
        {description === undefined ? null : (
          <p className="max-w-sm text-sm text-slate-500">{description}</p>
        )}
      </div>

      {action === undefined ? null : <div className="mt-2">{action}</div>}
    </div>
  );
}
